interface ArtifactLike {
  ts?: unknown;
  primary_language?: unknown;
  path_category_dominant?: unknown;
  path_category_counts?: unknown;
  size_bucket?: unknown;
  change_type?: unknown;
  author_type?: unknown;
}

export interface SlicesSummary {
  ts: string | null;
  primary_language: string | null;
  path_category_dominant: string | null;
  path_category_counts: Record<string, number> | null;
  size_bucket: string | null;
  change_type: string | null;
  author_type: string | null;
}

function isCountMap(v: unknown): v is Record<string, number> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  // Reject host objects (Map, Date, Set, etc.) — only accept plain objects.
  // Their `Object.values` returns `[]`, which vacuously passes the count
  // check, so we'd otherwise misclassify them.
  if (Object.getPrototypeOf(v) !== Object.prototype) return false;
  // Counts must be non-negative integers. Reject NaN, Infinity, negatives,
  // and fractions so malformed data doesn't render in the UI.
  return Object.values(v as Record<string, unknown>).every(
    (n) => typeof n === 'number' && Number.isInteger(n) && n >= 0
  );
}

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

export function summarizeSlices(artifacts: ArtifactLike[]): SlicesSummary {
  const sorted = [...artifacts]
    .filter((a) => typeof a.ts === 'string')
    .sort((a, b) => (a.ts as string).localeCompare(b.ts as string));
  const latest = sorted.at(-1);

  if (!latest) {
    return {
      ts: null,
      primary_language: null,
      path_category_dominant: null,
      path_category_counts: null,
      size_bucket: null,
      change_type: null,
      author_type: null,
    };
  }

  return {
    ts: strOrNull(latest.ts),
    // primary_language collapses both "field absent" (legacy rows) and
    // "explicit null from a cache-miss" into `null`. The UI renders both
    // as a dash. If we ever need to distinguish them, extend the return
    // type to `string | null | undefined` and the caller-side check.
    primary_language: typeof latest.primary_language === 'string' ? latest.primary_language : null,
    path_category_dominant: strOrNull(latest.path_category_dominant),
    path_category_counts: isCountMap(latest.path_category_counts) ? latest.path_category_counts : null,
    size_bucket: strOrNull(latest.size_bucket),
    change_type: strOrNull(latest.change_type),
    author_type: strOrNull(latest.author_type),
  };
}

function fmtCounts(counts: Record<string, number>): string {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${k}: ${n}`)
    .join(', ');
}

function Pill({ value }: { value: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 528,
        background: 'var(--loop-neutral-200)',
        color: 'var(--loop-neutral-800)',
      }}
    >
      {value}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
      <th scope="row" style={{ paddingTop: 4, paddingBottom: 4, paddingRight: 16, width: 160, color: 'var(--loop-neutral-600)', fontWeight: 528, textAlign: 'left' }}>{label}</th>
      <td style={{ paddingTop: 4, paddingBottom: 4 }}>{children}</td>
    </tr>
  );
}

const dash = <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>;

export interface SlicingPanelProps {
  artifacts: ArtifactLike[];
}

export function SlicingPanel({ artifacts }: SlicingPanelProps) {
  const s = summarizeSlices(artifacts);

  if (!s.ts) {
    return <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>No slice data yet.</p>;
  }

  const countsLabel = s.path_category_counts ? fmtCounts(s.path_category_counts) : '';

  return (
    <table style={{ fontSize: '0.875rem', width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        <Row label="Primary language">
          {s.primary_language ? <Pill value={s.primary_language} /> : dash}
        </Row>
        <Row label="Path category">
          {s.path_category_dominant ? <Pill value={s.path_category_dominant} /> : dash}
          {countsLabel && (
            <span style={{ color: 'var(--loop-neutral-600)', marginLeft: 8, fontSize: '0.75rem' }}>
              ({countsLabel})
            </span>
          )}
        </Row>
        <Row label="Size bucket">
          {s.size_bucket ? <Pill value={s.size_bucket} /> : dash}
        </Row>
        <Row label="Change type">
          {s.change_type ? <Pill value={s.change_type} /> : dash}
        </Row>
        <Row label="Author type">
          {s.author_type ? <Pill value={s.author_type} /> : dash}
        </Row>
      </tbody>
    </table>
  );
}
